"use client";

import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "@/components/flightSearchSection/DatePicker";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { isBefore } from "@/utils/dateFormatter";

const FerryDatePicker = ({
  activeTab, // "oneway" | "roundtrip"
  departureDate,
  returnDate,
  setDepartureDate,
  setReturnDate,
  publicHolidays,
  onOpen,
  validationErrors,
  setActiveTab,
  scrollToForm,
  viewportHeightWrapperActive = false,
}) => {
  const tForm = useTranslationDetector("ferry");
  const isMobile = useIsMobile();
  const containerRef = useRef(null);
  const departureDatePickerRef = useRef(null);
  const returnDatePickerRef = useRef(null);

  // Portal state'lerini takip et
  const [, setIsDepartureDatePickerOpen] = useState(false);
  const [, setIsReturnDatePickerOpen] = useState(false);
  const [isReturnDateSetByuser, setIsReturnDateSetByuser] = useState(false);

  // Stabilize activeTab with ref to prevent callback recreation
  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const onSelectDepartureDate = useCallback(
    (date) => {
      setDepartureDate(date);
      // Oneway modunda returnDate'i set etme, roundtrip'te set et
      if (activeTab === "roundtrip") {
        if (!isReturnDateSetByuser) {
          const twoDaysAfter = new Date(date);
          twoDaysAfter.setDate(date.getDate() + 1);
          setReturnDate(twoDaysAfter);
        } else {
          if (isBefore(returnDate, date)) {
            setReturnDate(date);
          }
        }

        // Return date picker'ı otomatik aç
        // setTimeout(() => {
        //   returnDatePickerRef.current?.open();
        // }, 100);
      }
    },
    [
      activeTab,
      isReturnDateSetByuser,
      returnDate,
      setDepartureDate,
      setReturnDate,
    ],
  );

  const onSelectReturnDate = useCallback(
    (date) => {
      setReturnDate(date);
      if (!isReturnDateSetByuser) {
        setIsReturnDateSetByuser(true);
      }
    },
    [isReturnDateSetByuser, setReturnDate],
  );

  // const onSelectReturnDate = useCallback(
  //   (date, triggerDate) => {
  //     // activeTab === "oneway" iken callback'i çağırma (sonsuz döngüyü önlemek için)
  //     // Bu kontrol gizli DateRangePicker'dan gelen çağrıları engeller
  //     if (activeTabRef.current === "oneway") {
  //       return;
  //     }

  //     // Edge case: Kullanıcı mevcut seçili bir tarihe tıkladı
  //     // AMA sadece her ikisi de doluysa yeni seçim başlat
  //     if (
  //       departureDate &&
  //       returnDate &&
  //       (isSameDay(triggerDate, departureDate) ||
  //         isSameDay(triggerDate, returnDate))
  //     ) {
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //       return;
  //     }

  //     if (
  //       departureDate &&
  //       !returnDate &&
  //       isBefore(triggerDate, departureDate)
  //     ) {
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //       return;
  //     }

  //     // Normal range seçimi devam ediyor
  //     if (!departureDate) {
  //       // İlk seçim
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //     } else if (!returnDate) {
  //       // İkinci seçim - return date belirleniyor
  //       if (isBefore(triggerDate, departureDate)) {
  //         // Geriye doğru seçim → Departure'ı güncelle
  //         setDepartureDate(triggerDate);
  //         setReturnDate(departureDate);
  //       } else {
  //         // İleriye doğru seçim veya aynı gün → Return'ü set et
  //         setReturnDate(triggerDate);
  //       }
  //       if (!isMobile) {
  //         returnDatePickerRef.current?.close();
  //       }
  //     } else {
  //       // Her iki tarih de dolu → Yeni seçim başlat
  //       setDepartureDate(triggerDate);
  //       setReturnDate(null);
  //     }
  //   },
  //   [setReturnDate, setDepartureDate, departureDate, returnDate, isMobile],
  // );

  const handleAddReturnClick = useCallback(() => {
    setActiveTab("roundtrip");
    // Return date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsReturnDatePickerOpen(true);
      returnDatePickerRef.current?.open();
    }, 100);
  }, [setActiveTab]);

  const handleMobileDepartureDateClick = useCallback(() => {
    // Return date picker'ı kapat ve state'i güncelle
    setIsReturnDatePickerOpen(false);
    returnDatePickerRef.current?.close();
    // Departure date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsDepartureDatePickerOpen(true);
      departureDatePickerRef.current?.open();
    }, 100);
  }, []);

  const handleMobileReturnDateClick = useCallback(() => {
    // Departure date picker'ı kapat ve state'i güncelle
    setIsDepartureDatePickerOpen(false);
    departureDatePickerRef.current?.close();
    // Return date picker'ı aç ve state'i güncelle
    setTimeout(() => {
      setIsReturnDatePickerOpen(true);
      returnDatePickerRef.current?.open();
    }, 150);
  }, []);

  // Desktop'te tarih alanına tıklandığında formu yukarı kaydır (scrollToForm true ise)
  const handleOpenWithScroll = useCallback(() => {
    if (!isMobile && scrollToForm && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      const targetPosition = rect.top + scrollTop - 200;

      window.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });
    }
  }, [isMobile, scrollToForm]);

  return (
    <>
      <div
        className="border-primary-weak flex w-full items-center border-b md:w-auto md:border-0"
        ref={containerRef}
        onClick={handleOpenWithScroll}
      >
        <div className="min-w-0 flex-1">
          <DatePicker
            ref={departureDatePickerRef}
            id="ferry-departure-date"
            label={tForm("departureDate")}
            drawerLabel={tForm("departureDateDrawerLabel")}
            onSelect={onSelectDepartureDate}
            value={departureDate}
            onPortalStateChange={setIsDepartureDatePickerOpen}
            isFromDate={true}
            defaultMonth={departureDate}
            mobileDepartureClick={handleMobileDepartureDateClick}
            mobileReturnClick={handleMobileReturnDateClick}
            departureDate={departureDate}
            returnDate={returnDate}
            isRoundTrip={false}
            publicHolidays={publicHolidays}
            onOpen={onOpen}
            cheapestFlightsMonthly={null}
            cheapestFlightsMonthlyByMonth={null}
            getCheapestFlightsMonthlyByMonth={null}
            oneWayCalendarPrices={null}
            viewportHeightWrapperActive={viewportHeightWrapperActive}
          />
        </div>
        <span className="relative flex h-16.5 w-10 shrink-0 items-center justify-center">
          <span className="bg-primary-weak absolute inset-y-0 left-1/2 w-px -translate-x-1/2"></span>
          <span className="border-primary-weak relative z-1 flex h-10 w-10 items-center justify-center rounded-full border bg-white">
            <FontAwesomeIcon
              icon={byPrefixAndName.fak["calendar-linex"]}
              className="text-base-line h-6 w-6"
            />
          </span>
        </span>
        <div className="-ml-5 min-w-0 flex-1 pr-1.5 pl-5">
          <button
            className={clsx(
              "text-foreground-muted h-full w-full cursor-pointer items-center justify-center gap-1 py-5",
              activeTab === "oneway" ? "flex" : "hidden",
            )}
            onClick={handleAddReturnClick}
          >
            <span className="flex h-5! w-5! items-center justify-center">
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["plusnew"]}
                className="h-5! w-5!"
              />
            </span>
            <span className="max-w-20.25 truncate font-medium">
              {tForm("addReturn")}
            </span>
          </button>
          <div
            className={clsx("relative", activeTab === "oneway" ? "hidden" : "")}
          >
            <DatePicker
              ref={returnDatePickerRef}
              id="ferry-return-date-hidden"
              label={tForm("returnDate")}
              drawerLabel={tForm("returnDateDrawerLabel")}
              onSelect={onSelectReturnDate}
              value={returnDate}
              disabled={departureDate}
              onPortalStateChange={setIsReturnDatePickerOpen}
              isFromDate={false}
              defaultMonth={departureDate}
              mobileDepartureClick={handleMobileDepartureDateClick}
              mobileReturnClick={handleMobileReturnDateClick}
              departureDate={departureDate}
              returnDate={returnDate}
              isRoundTrip={true}
              publicHolidays={publicHolidays}
              onOpen={onOpen}
              error={validationErrors?.returnDate}
              cheapestFlightsMonthly={null}
              cheapestFlightsMonthlyByMonth={null}
              getCheapestFlightsMonthlyByMonth={null}
              oneWayCalendarPrices={null}
              translationKey="ferry"
              viewportHeightWrapperActive={viewportHeightWrapperActive}
            />
            <button
              className="absolute top-[14.5px] right-[14.5px] cursor-pointer"
              onClick={() => setActiveTab("oneway")}
            >
              <FontAwesomeIcon
                icon={byPrefixAndName.fak["clear-dark"]}
                className="text-foreground text-[18px]"
              />
            </button>
          </div>
        </div>
      </div>

      {/* Her zaman render et ama görünürlüğünü kontrol et - handleAddReturnClick için gerekli */}
    </>
  );
};

export default FerryDatePicker;
